import React from 'react';
import { Box } from '@mui/material';
import { ElementRenderer } from '@geneo2-web/shared-ui';
import { Question } from '@protos/content_management/content.db_pb';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { ConceptTopicChip } from '../ConceptTopicChip';

export type ReviewResultStatus =
  | 'correct'
  | 'incorrect'
  | 'partial'
  | 'skipped'
  | 'timeout';

type ReviewBoxSize = 'default' | 'large';

interface QuestionAnswerReviewBoxProps {
  questionNumber: number;
  resultStatus: ReviewResultStatus;
  question?: Question;
  conceptLabel?: string;
  conceptEmoji?: string;
  size?: ReviewBoxSize;
  /** When true, removes bottom radius/shadow for stacking solution/footer below */
  flattenBottom?: boolean;
  /** When true, shows the difficulty/time/confidence meta strip below the header */
  showStats?: boolean;
  difficulty?: string;
  timeTakenSeconds?: number;
  /** `label` is an i18n key; `emoji` is rendered as-is */
  confidence?: { emoji: string; label: string };
  children: React.ReactNode;
}

const DIFFICULTY_TAG_STYLES: Record<string, { bgcolor: string; color: string }> =
  {
    easy: { bgcolor: '#dcfce7', color: '#15803d' },
    medium: { bgcolor: '#fef3c7', color: '#b45309' },
    hard: { bgcolor: '#fee2e2', color: '#b91c1c' },
  };

const formatReviewTime = (seconds: number) => {
  const safe = Math.max(0, Math.round(seconds));
  const m = Math.floor(safe / 60);
  const s = safe % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

const SIZE_STYLES: Record<
  ReviewBoxSize,
  {
    headerPx: number;
    headerPy: number;
    contentPx: number;
    contentPy: number;
    qFontSize: number;
    questionFontSize: string;
    statusFontSize: number;
  }
> = {
  default: {
    headerPx: 2.5,
    headerPy: 2,
    contentPx: 2.5,
    contentPy: 2,
    qFontSize: 13,
    questionFontSize: '14px',
    statusFontSize: 11,
  },
  large: {
    headerPx: 3.5,
    headerPy: 2.5,
    contentPx: 3.5,
    contentPy: 2.5,
    qFontSize: 15,
    questionFontSize: '16px',
    statusFontSize: 12,
  },
};

const STATUS_STYLES: Record<
  ReviewResultStatus,
  { bgcolor: string; color: string; icon: React.ReactNode; labelKey: string }
> = {
  correct: {
    bgcolor: '#dcfce7',
    color: '#16a34a',
    icon: <CheckIcon sx={{ fontSize: 14 }} />,
    labelKey: 'PAL_STAT_CORRECT',
  },
  incorrect: {
    bgcolor: '#fee2e2',
    color: '#dc2626',
    icon: <CloseIcon sx={{ fontSize: 14 }} />,
    labelKey: 'PAL_STAT_INCORRECT',
  },
  partial: {
    bgcolor: '#fef3c7',
    color: '#d97706',
    icon: null,
    labelKey: 'PAL_AR_PARTIAL',
  },
  skipped: {
    bgcolor: '#fef3c7',
    color: '#d97706',
    icon: null,
    labelKey: 'PAL_STAT_SKIPPED',
  },
  timeout: {
    bgcolor: '#fef3c7',
    color: '#d97706',
    icon: null,
    labelKey: 'PAL_STAT_TIMED_OUT',
  },
};

const Q_BADGE_STYLES: Record<
  ReviewResultStatus,
  { bgcolor: string; color: string }
> = {
  correct: { bgcolor: '#dcfce7', color: '#16a34a' },
  incorrect: { bgcolor: '#fee2e2', color: '#dc2626' },
  partial: { bgcolor: '#fef3c7', color: '#d97706' },
  skipped: { bgcolor: '#fef3c7', color: '#d97706' },
  timeout: { bgcolor: '#fef3c7', color: '#d97706' },
};

export const QuestionAnswerReviewBox = ({
  questionNumber,
  resultStatus,
  question,
  conceptLabel,
  conceptEmoji,
  size = 'default',
  flattenBottom = false,
  showStats = false,
  difficulty,
  timeTakenSeconds,
  confidence,
  children,
}: QuestionAnswerReviewBoxProps) => {
  const { t } = useTranslation();
  const difficultyKey = difficulty?.trim().toLowerCase() ?? '';
  const difficultyStyle = DIFFICULTY_TAG_STYLES[difficultyKey] ?? {
    bgcolor: '#f1f5f9',
    color: '#475569',
  };
  const showMetaRow =
    showStats &&
    (Boolean(difficulty) ||
      typeof timeTakenSeconds === 'number' ||
      Boolean(confidence));
  const elements =
    question?.question?.model?.value?.commonQuestionContent?.elements ?? [];
  const statusStyle = STATUS_STYLES[resultStatus];
  const qBadgeStyle = Q_BADGE_STYLES[resultStatus];
  const sizeStyle = SIZE_STYLES[size];

  return (
    <Box
      sx={{
        bgcolor: '#fff',
        borderRadius: flattenBottom ? '20px 20px 0 0' : '20px',
        boxShadow: flattenBottom ? 'none' : '0 2px 12px rgba(0, 0, 0, 0.06)',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          bgcolor: '#fafbfc',
          borderBottom: '1px solid #f1f5f9',
          px: sizeStyle.headerPx,
          py: sizeStyle.headerPy,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.75,
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: qBadgeStyle.bgcolor,
              color: qBadgeStyle.color,
              fontSize: sizeStyle.qFontSize,
              fontWeight: 800,
              borderRadius: '10px',
              lineHeight: 1,
              flexShrink: 0,
            }}
          >
            Q{questionNumber}
          </Box>

          {elements.length > 0 && (
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                pt: '5px',
                color: '#1e293b',
                '& .MuiTypography-root': {
                  fontSize: `${sizeStyle.questionFontSize} !important`,
                  fontWeight: '600 !important',
                  lineHeight: '1.5 !important',
                  color: '#1e293b',
                },
                '& p': { m: 0 },
              }}
            >
              <ElementRenderer elements={elements} disableInput />
            </Box>
          )}

          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              bgcolor: statusStyle.bgcolor,
              color: statusStyle.color,
              fontSize: sizeStyle.statusFontSize,
              fontWeight: resultStatus === 'incorrect' ? 800 : 700,
              px: 1.25,
              py: 0.5,
              borderRadius: 100,
              textTransform: 'uppercase',
              letterSpacing: 0.4,
              lineHeight: 1.3,
              flexShrink: 0,
              mt: 0.125,
            }}
          >
            {statusStyle.icon}
            {t(statusStyle.labelKey)}
          </Box>
        </Box>
        {conceptLabel && (
          <ConceptTopicChip label={conceptLabel} emoji={conceptEmoji} />
        )}
      </Box>

      {showMetaRow && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '14px',
            px: sizeStyle.headerPx,
            py: '12px',
            borderBottom: '1px solid #f1f5f9',
            bgcolor: '#fcfcfd',
          }}
        >
          {difficulty && (
            <Box
              sx={{
                px: '9px',
                py: '3px',
                borderRadius: '6px',
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: 0.4,
                textTransform: 'uppercase',
                bgcolor: difficultyStyle.bgcolor,
                color: difficultyStyle.color,
              }}
            >
              {difficulty}
            </Box>
          )}
          {typeof timeTakenSeconds === 'number' && (
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: 12,
                fontWeight: 600,
                color: '#64748b',
              }}
            >
              ⏱
              <Box component="strong" sx={{ color: '#1e293b', fontWeight: 700 }}>
                {formatReviewTime(timeTakenSeconds)}
              </Box>
            </Box>
          )}
          {confidence && (
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: 12,
                fontWeight: 600,
                color: '#64748b',
              }}
            >
              <Box component="span" sx={{ fontSize: 14, lineHeight: 1 }}>
                {confidence.emoji}
              </Box>
              <Box component="strong" sx={{ color: '#1e293b', fontWeight: 700 }}>
                {t(confidence.label)}
              </Box>
            </Box>
          )}
        </Box>
      )}

      <Box
        sx={{
          px: sizeStyle.contentPx,
          py: sizeStyle.contentPy,
          bgcolor: '#fff',
        }}
      >
        {children}
      </Box>
    </Box>
  );
};
